import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { toast } from "sonner";
import axios from "axios";
import { 
  Bell, 
  Check, 
  X, 
  TrendingUp,
  TrendingDown,
  Clock, 
  RefreshCw,
  Settings,
  AlertTriangle,
  Zap,
  Calendar,
  Smartphone
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AlertCard = ({ alert, onApprove, onReject, loading }) => {
  const isPositiveChange = alert.change_percent > 0;
  const isPending = alert.status === "pending";
  const TrendIcon = isPositiveChange ? TrendingUp : TrendingDown;
  
  return (
    <Card className={`transition-all ${isPending ? "border-amber-400 dark:border-amber-500 border-2" : ""}`} data-testid={`alert-${alert.id}`}>
      <CardContent className="p-4 sm:p-6">
        {/* Mobile-optimized layout */}
        <div className="space-y-4">
          {/* Header with room name and status */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                isPending ? "bg-amber-100 dark:bg-amber-900/30" : 
                alert.status === "approved" ? "bg-emerald-100 dark:bg-emerald-900/30" :
                "bg-red-100 dark:bg-red-900/30"
              }`}>
                {isPending ? (
                  <Bell className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600 dark:text-amber-400" />
                ) : alert.status === "approved" ? (
                  <Check className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600 dark:text-emerald-400" />
                ) : (
                  <X className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 dark:text-red-400" />
                )}
              </div>
              <div>
                <h4 className="font-bold text-base sm:text-lg">{alert.room_name}</h4>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant={isPending ? "default" : "secondary"} className={`text-xs ${isPending ? "bg-amber-500" : ""}`}>
                    {isPending ? "PENDIENTE" : alert.status === "approved" ? "Aprobado" : "Rechazado"}
                  </Badge>
                  {alert.target_date && (
                    <Badge variant="outline" className="text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-300">
                      📅 {format(new Date(alert.target_date + "T12:00:00"), "dd MMM yyyy", { locale: es })}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Message */}
          <p className="text-sm sm:text-base text-muted-foreground">{alert.message}</p>
          
          {/* Price comparison - Mobile optimized */}
          <div className="bg-muted/50 rounded-xl p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="text-center flex-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Actual</p>
                <p className="font-mono text-xl sm:text-2xl font-bold mt-1">€{alert.current_value}</p>
              </div>
              
              <div className="flex flex-col items-center">
                <TrendIcon className={`w-6 h-6 sm:w-8 sm:h-8 ${isPositiveChange ? "text-emerald-500" : "text-red-500"}`} />
                <Badge 
                  variant="outline" 
                  className={`mt-1 text-xs sm:text-sm font-bold ${isPositiveChange ? "text-emerald-600 border-emerald-600" : "text-red-600 border-red-600"}`}
                >
                  {isPositiveChange ? "+" : ""}{alert.change_percent}%
                </Badge>
              </div>
              
              <div className="text-center flex-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Sugerido</p>
                <p className={`font-mono text-xl sm:text-2xl font-bold mt-1 ${isPositiveChange ? "text-emerald-600" : "text-red-600"}`}>
                  €{alert.suggested_value}
                </p>
              </div>
            </div>
          </div>
          
          {/* Reason */}
          <p className="text-xs sm:text-sm text-muted-foreground bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border-l-4 border-blue-500">
            {alert.reason}
          </p>
          
          {/* Action buttons - Big for mobile */}
          {isPending && (
            <div className="grid grid-cols-2 gap-3 pt-2">
              <Button 
                size="lg"
                onClick={() => onApprove(alert.id)}
                disabled={loading}
                className="bg-emerald-600 hover:bg-emerald-700 text-white h-14 text-base font-bold"
                data-testid={`approve-${alert.id}`}
              >
                <Check className="w-5 h-5 mr-2" />
                APROBAR
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => onReject(alert.id)}
                disabled={loading}
                className="h-14 text-base font-bold border-2"
                data-testid={`reject-${alert.id}`}
              >
                <X className="w-5 h-5 mr-2" />
                RECHAZAR
              </Button>
            </div>
          )}
          
          {/* Timestamp */}
          <p className="text-xs text-muted-foreground text-center pt-2">
            {format(new Date(alert.created_at), "dd MMM yyyy, HH:mm", { locale: es })}
            {alert.resolved_at && ` • Resuelto: ${format(new Date(alert.resolved_at), "dd MMM, HH:mm", { locale: es })}`}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

const Alerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [pendingAlerts, setPendingAlerts] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [config, setConfig] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [processingAlert, setProcessingAlert] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [alertsRes, pendingRes, statsRes, configRes, schedulerRes] = await Promise.all([
        axios.get(`${API}/alerts`),
        axios.get(`${API}/alerts/pending`),
        axios.get(`${API}/alerts/stats`),
        axios.get(`${API}/sync/config`),
        axios.get(`${API}/scheduler/status`)
      ]);
      setAlerts(alertsRes.data);
      setPendingAlerts(pendingRes.data);
      setStats(statsRes.data);
      setConfig(configRes.data);
      setSchedulerStatus(schedulerRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleApprove = async (alertId) => {
    setProcessingAlert(alertId);
    try {
      await axios.post(`${API}/alerts/${alertId}/approve`);
      toast.success("Cambio de precio aplicado");
      fetchData();
    } catch (error) {
      toast.error("Error al aprobar alerta");
    } finally {
      setProcessingAlert(null);
    }
  };

  const handleReject = async (alertId) => {
    setProcessingAlert(alertId);
    try {
      await axios.post(`${API}/alerts/${alertId}/reject`);
      toast.success("Alerta rechazada");
      fetchData();
    } catch (error) {
      toast.error("Error al rechazar alerta");
    } finally {
      setProcessingAlert(null);
    }
  };

  const handleRunSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/sync/run`);
      if (response.data.success) {
        const alertsCreated = response.data.alerts_created || 0;
        if (alertsCreated > 0) {
          toast.success(`Sincronización completada. ${alertsCreated} nueva(s) alerta(s) creada(s)`);
        } else {
          toast.success("Sincronización completada. Sin cambios detectados");
        }
        fetchData();
      } else {
        toast.error(response.data.message || "Error en sincronización");
      }
    } catch (error) {
      toast.error("Error al sincronizar");
    } finally {
      setSyncing(false);
    }
  };

  const handleConfigChange = async (key, value) => {
    try {
      const updateData = { [key]: value };
      await axios.put(`${API}/sync/config`, updateData);
      setConfig(prev => ({ ...prev, [key]: value }));
      toast.success("Configuración actualizada");
    } catch (error) {
      toast.error("Error al actualizar configuración");
    }
  };

  const handleSchedulerToggle = async (enabled) => {
    try {
      if (enabled) {
        await axios.post(`${API}/scheduler/start`);
        toast.success("Sincronización automática activada");
      } else {
        await axios.post(`${API}/scheduler/stop`);
        toast.success("Sincronización automática desactivada");
      }
      // Refresh status
      const statusRes = await axios.get(`${API}/scheduler/status`);
      setSchedulerStatus(statusRes.data);
    } catch (error) {
      toast.error("Error al cambiar estado del scheduler");
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="alerts-page">
      {/* Header - Mobile optimized */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Alertas</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Gestiona los cambios de precio</p>
        </div>
        <Button 
          onClick={handleRunSync} 
          disabled={syncing} 
          className="w-full sm:w-auto h-12 text-base font-semibold"
          data-testid="run-sync-btn"
        >
          {syncing ? (
            <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <Zap className="w-5 h-5 mr-2" />
          )}
          Sincronizar con Oscar
        </Button>
      </div>

      {/* Stats Cards - Mobile grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <Card className={`${pendingAlerts.length > 0 ? "border-amber-400 border-2 bg-amber-50 dark:bg-amber-900/10" : ""}`}>
          <CardContent className="p-4 sm:pt-6">
            <div className="flex flex-col items-center sm:flex-row sm:justify-between">
              <div className="text-center sm:text-left">
                <p className="text-xs sm:text-sm text-muted-foreground">Pendientes</p>
                <p className="text-3xl sm:text-4xl font-bold">{stats.pending}</p>
              </div>
              <Bell className={`w-6 h-6 sm:w-8 sm:h-8 mt-2 sm:mt-0 ${pendingAlerts.length > 0 ? "text-amber-500 animate-pulse" : "text-muted-foreground"}`} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 sm:pt-6">
            <div className="flex flex-col items-center sm:flex-row sm:justify-between">
              <div className="text-center sm:text-left">
                <p className="text-xs sm:text-sm text-muted-foreground">Aprobadas</p>
                <p className="text-3xl sm:text-4xl font-bold text-emerald-600">{stats.approved}</p>
              </div>
              <Check className="w-6 h-6 sm:w-8 sm:h-8 mt-2 sm:mt-0 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 sm:pt-6">
            <div className="flex flex-col items-center sm:flex-row sm:justify-between">
              <div className="text-center sm:text-left">
                <p className="text-xs sm:text-sm text-muted-foreground">Rechazadas</p>
                <p className="text-3xl sm:text-4xl font-bold text-red-600">{stats.rejected}</p>
              </div>
              <X className="w-6 h-6 sm:w-8 sm:h-8 mt-2 sm:mt-0 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 sm:pt-6">
            <div className="flex flex-col items-center sm:flex-row sm:justify-between">
              <div className="text-center sm:text-left">
                <p className="text-xs sm:text-sm text-muted-foreground">Total</p>
                <p className="text-3xl sm:text-4xl font-bold">{stats.total}</p>
              </div>
              <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 mt-2 sm:mt-0 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList className="w-full grid grid-cols-3 h-12">
          <TabsTrigger value="pending" className="relative text-sm sm:text-base py-3">
            Pendientes
            {pendingAlerts.length > 0 && (
              <span className="absolute -top-1 -right-1 sm:right-2 w-5 h-5 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                {pendingAlerts.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="history" className="text-sm sm:text-base py-3">Historial</TabsTrigger>
          <TabsTrigger value="config" className="text-sm sm:text-base py-3">Config</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4">
          {pendingAlerts.length === 0 ? (
            <Card className="p-12 text-center">
              <Check className="w-12 h-12 mx-auto text-emerald-500 mb-4" />
              <h3 className="text-lg font-medium mb-2">Sin alertas pendientes</h3>
              <p className="text-muted-foreground">
                Todas las propuestas de cambio han sido procesadas
              </p>
            </Card>
          ) : (
            <div className="space-y-4">
              {pendingAlerts.map((alert) => (
                <AlertCard
                  key={alert.id}
                  alert={alert}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  loading={processingAlert === alert.id}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          {alerts.length === 0 ? (
            <Card className="p-12 text-center">
              <Clock className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Sin historial</h3>
              <p className="text-muted-foreground">
                Las alertas procesadas aparecerán aquí
              </p>
            </Card>
          ) : (
            <div className="space-y-4">
              {alerts.map((alert) => (
                <AlertCard
                  key={alert.id}
                  alert={alert}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  loading={false}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="config" className="space-y-6">
          {/* Scheduler Status Card */}
          <Card className={schedulerStatus?.enabled ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-900/10" : "border-amber-400 bg-amber-50 dark:bg-amber-900/10"}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className={`w-5 h-5 ${schedulerStatus?.enabled ? "text-emerald-500" : "text-amber-500"}`} />
                Sincronización Automática
                <Badge variant={schedulerStatus?.enabled ? "default" : "secondary"} className={schedulerStatus?.enabled ? "bg-emerald-500" : ""}>
                  {schedulerStatus?.enabled ? "ACTIVA" : "INACTIVA"}
                </Badge>
              </CardTitle>
              <CardDescription>
                {schedulerStatus?.enabled 
                  ? `Sincronizando cada ${schedulerStatus?.interval_minutes} minutos. Próxima ejecución: ${schedulerStatus?.next_run ? format(new Date(schedulerStatus.next_run), "HH:mm", { locale: es }) : "pendiente"}`
                  : "Activa la sincronización automática para recibir alertas cuando se venda una habitación"
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-semibold">Activar Sincronización Automática</Label>
                  <p className="text-sm text-muted-foreground">
                    El sistema sincronizará con Oscar cada {config?.interval_minutes || 15} minutos
                  </p>
                </div>
                <Switch
                  checked={schedulerStatus?.enabled || false}
                  onCheckedChange={handleSchedulerToggle}
                  data-testid="scheduler-toggle"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Configuración de Sincronización
              </CardTitle>
              <CardDescription>
                Configura cómo y cuándo el sistema sincroniza con Oscar y genera alertas
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Sync Until Date */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Sincronizar Hasta</Label>
                  <p className="text-sm text-muted-foreground">
                    Fecha límite para la sincronización automática
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <Input
                    type="date"
                    value={config?.sync_until || "2026-12-31"}
                    onChange={(e) => handleConfigChange("sync_until", e.target.value)}
                    className="w-40"
                    data-testid="sync-until-input"
                  />
                </div>
              </div>

              {/* Sync Interval */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Intervalo de Sincronización</Label>
                  <p className="text-sm text-muted-foreground">
                    Cada cuántos minutos se sincroniza con Oscar
                  </p>
                </div>
                <Select
                  value={String(config?.interval_minutes || 15)}
                  onValueChange={(value) => handleConfigChange("interval_minutes", parseInt(value))}
                >
                  <SelectTrigger className="w-32" data-testid="interval-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 minutos</SelectItem>
                    <SelectItem value="15">15 minutos</SelectItem>
                    <SelectItem value="30">30 minutos</SelectItem>
                    <SelectItem value="60">1 hora</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Auto Approve */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Modo Automático</Label>
                  <p className="text-sm text-muted-foreground">
                    Aplicar cambios de precio automáticamente (sin confirmación)
                  </p>
                </div>
                <Switch
                  checked={config?.auto_approve || false}
                  onCheckedChange={(checked) => handleConfigChange("auto_approve", checked)}
                  data-testid="auto-approve-switch"
                />
              </div>

              {!config?.auto_approve && (
                <Alert>
                  <Bell className="h-4 w-4" />
                  <AlertTitle>Modo Confirmación Activo</AlertTitle>
                  <AlertDescription>
                    Cuando se detecte una venta o cambio de ocupación, el sistema te preguntará 
                    antes de aplicar cualquier cambio de precio. Recibirás una alerta que deberás 
                    aprobar o rechazar manualmente.
                  </AlertDescription>
                </Alert>
              )}

              {config?.auto_approve && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Modo Automático Activo</AlertTitle>
                  <AlertDescription>
                    Los cambios de precio se aplicarán automáticamente sin confirmación. 
                    Esto es útil cuando no puedes estar pendiente, pero ten cuidado con 
                    cambios bruscos de precio.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* How it works */}
          <Card>
            <CardHeader>
              <CardTitle>¿Cómo Funciona?</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold">1</span>
                  </div>
                  <div>
                    <h4 className="font-medium">Sincronización con Oscar</h4>
                    <p className="text-sm text-muted-foreground">
                      El sistema lee la disponibilidad actual de Oscar cada {config?.interval_minutes || 15} minutos
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold">2</span>
                  </div>
                  <div>
                    <h4 className="font-medium">Detección de Ventas</h4>
                    <p className="text-sm text-muted-foreground">
                      Cuando detecta que se ha vendido una habitación (ocupación sube), genera una alerta
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
                    <Bell className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">Propuesta de Precio</h4>
                    <p className="text-sm text-muted-foreground">
                      Te propone subir el precio según las reglas configuradas y espera tu aprobación
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">Aprobación/Rechazo</h4>
                    <p className="text-sm text-muted-foreground">
                      Tú decides si aplicas el cambio o lo rechazas
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Alerts;
